import { useCallback } from 'react'
import useSWR, { ScopedMutator, useSWRConfig } from 'swr'
import { unstable_serialize } from 'swr/infinite'
import {
  LibraryCollectionDetail,
  LibraryItemRef,
  UpdateLibraryCollectionBody,
  toLibraryMemberStoredKind,
} from '@digital-twins/shared-types'

import { libraryCollectionsApi } from '@/lib/api'
import { libraryCollectionsListKey } from '@/pages/KnowledgeCollectionsListPage/useLibraryCollectionsList'

export const LIBRARY_COLLECTION_SWR_KEY = 'library/collection'
export const LIBRARY_COLLECTION_KPIS_SWR_KEY = 'library/collection-kpis'
export const LIBRARY_COLLECTION_TOPICS_SWR_KEY = 'library/collection-topics'
export const LIBRARY_ITEM_COLLECTIONS_SWR_KEY = 'library/item-collections'

/** SWR keys `[name, collectionToken, ...]` that read one collection (not paged). */
const COLLECTION_SCOPED_KEYS: ReadonlyArray<string> = [
  LIBRARY_COLLECTION_SWR_KEY,
  LIBRARY_COLLECTION_KPIS_SWR_KEY,
  LIBRARY_COLLECTION_TOPICS_SWR_KEY,
]

/** The HTTP status of an axios-shaped error, or null. */
export function httpStatusOf(error: unknown): number | null {
  if (error == null || typeof error !== 'object') {
    return null
  }
  const status = (error as { response?: { status?: unknown } }).response?.status
  return typeof status === 'number' ? status : null
}

/** True for an item collections key `[name, storedKind, token]` that one of `items` reads. */
function isItemCollectionsKeyOf(key: Array<unknown>, items: Array<LibraryItemRef> | undefined): boolean {
  if (key[0] !== LIBRARY_ITEM_COLLECTIONS_SWR_KEY) {
    return false
  }
  if (!items) {
    return true
  }
  return items.some((item) => key[1] === toLibraryMemberStoredKind(item.kind) && key[2] === item.token)
}

/**
 * Refetches what a write to one collection can change: its detail, KPIs and
 * Topics, the paged list, and the item collections lines. With `items` (a
 * membership write) only those items' lines refetch; without it (a rules
 * edit, which can move any item) every line does. The paged resources
 * sections refetch through their own `refresh` (an infinite key holds its
 * filters, so no matcher reaches it). `mutateScoped` is
 * `useSWRConfig().mutate`, so the write reaches the cache the hooks read.
 */
export async function revalidateLibraryCollection(
  mutateScoped: ScopedMutator,
  token: string,
  items?: Array<LibraryItemRef>,
): Promise<void> {
  await Promise.all([
    mutateScoped(
      (key) =>
        Array.isArray(key) &&
        ((COLLECTION_SCOPED_KEYS.includes(key[0]) && key[1] === token) || isItemCollectionsKeyOf(key, items)),
    ),
    mutateScoped(unstable_serialize(libraryCollectionsListKey)),
  ])
}

export interface UseLibraryCollectionResult {
  collection: LibraryCollectionDetail | null
  isLoading: boolean
  hasError: boolean
  /** The token is unknown to the viewer (404), or not a token at all (400). */
  isNotFound: boolean
  refresh: () => Promise<unknown>
  /** PATCH with only the changed fields, then mutate the detail. */
  update: (body: UpdateLibraryCollectionBody) => Promise<LibraryCollectionDetail>
  /** DELETE; the caller navigates away. */
  remove: () => Promise<void>
}

/**
 * One collection's detail over `GET /library/collections/:token`, keyed by
 * token. A null token reads nothing. Writes put the response in the cache
 * and refetch the KPIs, Topics and the list.
 */
export function useLibraryCollection(token: string | null): UseLibraryCollectionResult {
  const { mutate: mutateScoped } = useSWRConfig()
  const { data, error, isLoading, mutate } = useSWR<LibraryCollectionDetail>(
    token ? [LIBRARY_COLLECTION_SWR_KEY, token] : null,
    () => libraryCollectionsApi.getCollection(token as string),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  )

  const update = useCallback(
    async (body: UpdateLibraryCollectionBody) => {
      if (!token) {
        throw new Error('useLibraryCollection: no collection to update')
      }
      const next = await libraryCollectionsApi.updateCollection(token, body)
      await mutate(next, { revalidate: false })
      await revalidateLibraryCollection(mutateScoped, token)
      return next
    },
    [token, mutate, mutateScoped],
  )

  const remove = useCallback(async () => {
    if (!token) {
      return
    }
    await libraryCollectionsApi.deleteCollection(token)
    await mutateScoped(unstable_serialize(libraryCollectionsListKey))
  }, [token, mutateScoped])

  const status = httpStatusOf(error)

  return {
    collection: data ?? null,
    isLoading: Boolean(token) && isLoading,
    hasError: Boolean(error),
    isNotFound: status === 404 || status === 400,
    refresh: () => mutate(),
    update,
    remove,
  }
}
